import type {SendAnalyticsEventFunction} from '@github-ui/use-analytics'
import {CircuitBreaker} from './circuit-breaker'
import {
  INTERNAL_ONLY_ATTRIBUTES,
  INVALID,
  type CacheHitSource,
  type CacheKey,
  type Invalid,
  type PersisterCallbacks,
  type QueryAttributes,
  type QueryParameters,
  type Storage,
} from './types'

import {HOT_CACHE_HIT_EVENT, InMemoryLRUQueryCache} from './in-memory-lru-query-cache'
import {CACHE_EVENTS} from '@github-ui/safe-indexed-db-storage'
import {initHPCData, tryReportPendingHPCEvent} from './hpc-metrics'
import {
  constructCacheEntry,
  dispatchCacheHitEvent,
  dispatchStaleCacheWarning,
  generateCacheKey,
  getUpdateType,
  hasErrors,
  removeInternalOnlyAttributes,
  reportQueryForInsightsApi,
  shouldClearCache,
  shouldSkipNetwork,
  shouldWriteToCache,
} from './helpers'

export {INTERNAL_ONLY_ATTRIBUTES, INVALID, dispatchCacheHitEvent, tryReportPendingHPCEvent}
export type {CacheHitSource}

const PREHEAT_NOOP_EVENT = 'offline_cache.preheat_noop'

interface StoragePersisterOptions {
  /** The storage client used for setting and retrieving items from cache.
   * For SSR pass in `undefined`.
   */
  storage: Storage | undefined | null
  /**
   * The max-allowed age of the cache in milliseconds.
   * If a persisted cache is found that is older than this
   * time, it will be discarded
   */
  maxAgeMs: number
  /**
   * Prefix to be used for storage key.
   * Storage key is a combination of prefix and query hash in a form of `prefix-queryHash`.
   */
  prefix: string
  /**
   * Function to send custom analytics events (that aren't handled by the storage client)
   */
  sendAnalyticsEvent: SendAnalyticsEventFunction
}

export type QueryCache<T> = ReturnType<typeof createQueryCache<T>>

export function retrieveQueryFromHotCache<T>(
  prefix: string,
  queryParameters: QueryParameters,
  defaultValues?: Record<string, unknown>,
  excludeFromCacheKey?: string[],
): T | undefined | Invalid {
  const cacheKey = generateCacheKey(prefix, queryParameters, defaultValues, excludeFromCacheKey)
  const hotCachedQuery = InMemoryLRUQueryCache.getItem<{queryHash: string; state: {data: T}}>(cacheKey)
  if (hotCachedQuery) {
    if (hotCachedQuery.queryHash !== queryParameters.queryId) {
      return INVALID
    }
    tryReportPendingHPCEvent(queryParameters, 'hit-hot')
    InMemoryLRUQueryCache.sendAnalyticsEvent(HOT_CACHE_HIT_EVENT, cacheKey, {})
    return hotCachedQuery.state.data
  }
  return undefined
}

export function createQueryCache<T>({storage, prefix, maxAgeMs, sendAnalyticsEvent}: StoragePersisterOptions) {
  const circuitBreakerKey = `${prefix}-circuit-breaker`
  const circuitBreaker = new CircuitBreaker(circuitBreakerKey, sendAnalyticsEvent)

  initHPCData(sendAnalyticsEvent)

  // Initialize hot cache with analytics for error reporting
  InMemoryLRUQueryCache.init(sendAnalyticsEvent)

  async function retrieveQuery(
    cacheKey: string,
    queryParameters: QueryParameters,
    attributes?: {[key: string]: unknown},
  ): Promise<T | undefined | Invalid> {
    if (!storage) {
      return undefined
    }
    try {
      const persistedQuery = await storage.getItem(cacheKey, removeInternalOnlyAttributes(attributes))
      if (persistedQuery && persistedQuery.queryHash !== queryParameters.queryId) {
        // The persisted query has changed and so the cached version is no longer valid
        return INVALID
      }
      return persistedQuery?.state.data as T | undefined
    } catch (error) {
      sendAnalyticsEvent(CACHE_EVENTS.CACHE_READ_ERROR, cacheKey, {error: String(error)})
      return undefined
    }
  }

  async function evictQuery(cacheKey: CacheKey) {
    if (!storage) {
      return
    }
    try {
      InMemoryLRUQueryCache.removeItem(cacheKey)
      await storage.removeItem(cacheKey)
    } catch (error) {
      sendAnalyticsEvent(CACHE_EVENTS.CACHE_EVICT_ERROR, cacheKey, {error: String(error)})
    }
  }

  async function tryCacheFetchResult(
    queryParameters: QueryParameters,
    fetchResult: T | null,
    ttl: number,
    attributes?: {[key: string]: unknown},
    defaultValues?: Record<string, unknown>,
    preheatSource?: string,
  ) {
    if (!storage) {
      return undefined
    }
    const keysToExclude = attributes?.[INTERNAL_ONLY_ATTRIBUTES.EXCLUDE_FROM_CACHE_KEY] as string[] | undefined
    const cacheKey = generateCacheKey(prefix, queryParameters, defaultValues, keysToExclude)
    const cacheEntry = constructCacheEntry(
      queryParameters,
      ttl,
      defaultValues,
      keysToExclude,
      preheatSource,
      fetchResult,
    )
    try {
      // only write to hot cache if preheating
      if (preheatSource) {
        InMemoryLRUQueryCache.setItem(cacheKey, cacheEntry)
      }
      await storage.setItem(
        cacheKey,
        cacheEntry,
        undefined,
        undefined,
        removeInternalOnlyAttributes({...attributes, query_name: queryParameters.queryName}),
      )
    } catch (error) {
      sendAnalyticsEvent(CACHE_EVENTS.CACHE_WRITE_ERROR, cacheKey, {error: String(error)})
    }
  }

  async function persisterFn(
    callbacks: PersisterCallbacks<T>,
    queryParameters: QueryParameters,
    ttl?: number,
    attributes?: QueryAttributes,
    defaultValues?: Record<string, unknown>,
  ): Promise<T | undefined> {
    const keysToExclude = attributes?.[INTERNAL_ONLY_ATTRIBUTES.EXCLUDE_FROM_CACHE_KEY]
    const cacheKey = generateCacheKey(prefix, queryParameters, defaultValues, keysToExclude)
    const cachedResult = await retrieveQuery(cacheKey, queryParameters, attributes)
    const hasCachedResult = cachedResult !== undefined && cachedResult !== INVALID
    const preheatSource = attributes?.preheat_source
    const preHeated = !!preheatSource
    const skipCache = !!attributes?.skip_cache
    const skipNetwork = !!attributes?.[INTERNAL_ONLY_ATTRIBUTES.SKIP_NETWORK]

    if (hasCachedResult) {
      reportQueryForInsightsApi(queryParameters)
      if (!preHeated && !skipCache) {
        tryReportPendingHPCEvent(queryParameters, 'hit')
        callbacks.preloadSubscriptions?.(cachedResult as T)
        callbacks.observer.next(cachedResult as T)
        // Dispatch cache hit event for staff bar indicator
        dispatchCacheHitEvent()
      }
    } else {
      tryReportPendingHPCEvent(queryParameters, 'miss')
    }

    if (preHeated && hasCachedResult) {
      const cacheEntry = constructCacheEntry(
        queryParameters,
        ttl ?? maxAgeMs,
        defaultValues,
        keysToExclude,
        preheatSource,
        cachedResult as T,
      )
      InMemoryLRUQueryCache.setItem(cacheKey, cacheEntry)
      // If preHeated is set and we have a cached result, don't return anything
      sendAnalyticsEvent(PREHEAT_NOOP_EVENT, cacheKey, {preheatSource})
    }
    if (shouldSkipNetwork(skipNetwork, preHeated, hasCachedResult, circuitBreaker)) {
      return
    }

    let fetchResult: T
    try {
      fetchResult = await callbacks.queryFn(hasCachedResult)
    } catch (error) {
      // If queryFn throws and we have cached data, emit stale cache warning
      // and return the cached result to prevent UI breakage
      if (hasCachedResult) {
        dispatchStaleCacheWarning([{message: error instanceof Error ? error.message : 'Unknown error'}])
        return cachedResult as T
      }
      // If no cached data, re-throw the error
      throw error
    }

    const responseHasErrors = hasErrors(fetchResult)

    // Emit stale cache warning if we served cached data but server returned errors
    if (hasCachedResult && responseHasErrors) {
      dispatchStaleCacheWarning((fetchResult as {errors: unknown[]}).errors)
    }

    if (preHeated) {
      circuitBreaker.handlePreheatResponse(responseHasErrors, fetchResult)
    }

    if (shouldClearCache<T>(fetchResult, cachedResult)) {
      evictQuery(cacheKey)
    }
    if (shouldWriteToCache<T>(fetchResult)) {
      const updateType = getUpdateType(cachedResult, fetchResult, preHeated, skipCache)
      tryCacheFetchResult(
        queryParameters,
        fetchResult,
        ttl ?? maxAgeMs,
        {...attributes, update_type: updateType},
        defaultValues,
        preheatSource,
      )
    }

    // For preheating requests, we only want the data in the cache
    // There's no point in populating the store (which is scoped to a session) and it can cause consistency issues
    // See https://github.com/github/issues/issues/19434
    if (preHeated) {
      return
    }
    // fetchResult will be defined if no error was thrown
    return fetchResult
  }

  return {persisterFn, retrieveQuery, tryCacheFetchResult}
}
